import { Icon } from "../Icon";
import { faEye, faEyeSlash } from "@/icons/regular";
import { cn } from "@/utils/cnHelper";
import * as React from "react";

interface InputProps extends Omit<React.ComponentProps<"input">, "theme"> {
  theme?: "dark" | "light";
  children?: React.ReactNode;
  radius?: "none" | "sm" | "md" | "lg" | "full";
  canTogglePassword?: boolean;
}

interface InputSlotProps {
  children: React.ReactNode;
}

/**
 * A styled input component that extends the native HTML input element.
 *
 * @description This component provides a consistent, themed input with enhanced styling
 * while maintaining full compatibility with all native HTML input attributes and behaviors.
 * Supports adding elements via the Input.Slot compound component.
 *
 * param {InputProps} props - All native HTML input props plus theme customization
 * param {("dark" | "light")} [props.theme="dark"] - Visual theme for the input
 * param {React.ReactNode} [props.children] - Input.Slot components
 * param {string} [props.className] - Additional CSS classes to apply
 * param {string} [props.type] - HTML input type (text, email, password, etc.)
 *
 
 *
 * @returns {JSX.Element} A styled input element that supports all native input functionality
 */

/*
 * example
 * ```tsx
 * // Basic usage with default dark theme
 * <Input placeholder="Enter text..." />
 * ```
 *
 *  example
 * ```tsx
 * // Light theme
 * <Input
 *   theme="light"
 *   type="email"
 *   placeholder="user@example.com"
 *   onChange={handleChange}
 * />
 * ```
 *  example
 * ```tsx
 * //Password input with toggle show password
 * <Input
 *   type="password"
 *   placeholder="Enter password"
 *   canTogglePassword={true}
 * />
 * ```
 *
 * example
 * ```tsx
 * //input with submit button
 * <Input
 *   type="text"
 *   placeholder="Enter details"
 * >
 *   <Input.Slot>
 *     <button onClick={submitForm}>Submit</button>
 *   </Input.Slot>
 * </Input>
 * ```
 */
function InputRoot({ className, type, theme = "dark", children, radius = "md", canTogglePassword = true, ...props }: InputProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const isPasswordType = type === "password";
  const shouldShowToggle = isPasswordType && canTogglePassword;

  const themeClasses =
    theme === "dark"
      ? "tw-bg-grey_scale-900 tw-text-white tw-border-grey_scale-600 tw-placeholder-grey_scale-400"
      : "tw-bg-white tw-text-grey_scale-800 tw-border-grey_scale-200 tw-placeholder-grey_scale-500";

  const focusClasses =
    "focus-visible:tw-outline-none focus:tw-outline-none focus-visible:!tw-outline-2 focus-visible:!tw-outline-solid focus-visible:!tw-outline-brand_scale-500 focus:!tw-outline-2 focus:!tw-outline-solid focus:!tw-outline-brand_scale-500 focus:tw-ring-0 focus:tw-ring-offset-0";

  const radiusClasses = {
    none: "tw-rounded-none",
    sm: "tw-rounded-sm",
    md: "tw-rounded-lg",
    lg: "tw-rounded-xl",
    full: "tw-rounded-full",
  }[radius];

  // Find slot content from children
  const slotContent = React.Children.toArray(children).find(
    child => React.isValidElement(child) && (child.type as any)?.displayName === "InputSlot"
  );

  // Password toggle button
  const passwordToggleButton = (
    <button
      type="button"
      aria-label={showPassword ? "Hide password" : "Show password"}
      onClick={() => setShowPassword(v => !v)}
      className={cn(
        "hover:tw-text-grey_scale-600 focus:tw-outline-none",
        theme === "dark" ? "tw-text-white" : "tw-text-grey_scale-800"
      )}
      tabIndex={0}
    >
      {showPassword ? <Icon icon={faEyeSlash} /> : <Icon icon={faEye} />}
    </button>
  );

  const inputElement = (
    <input
      type={shouldShowToggle ? (showPassword ? "text" : "password") : type}
      data-slot="input"
      className={cn(
        "tw-w-full tw-border !tw-px-4 !tw-py-2  tw-text-base tw-placeholder-grey_scale-400  ",
        "tw-file:inline-flex tw-file:h-7 tw-file:border-0 tw-file:bg-transparent tw-file:text-sm tw-file:font-medium tw-file:bg-brand_scale-500 tw-file:text-white tw-file:rounded-lg tw-file:px-4 tw-file:py-2 tw-file:font-bold tw-file:hover:bg-brand_scale-700 tw-selection:bg-primary tw-selection:text-primary-foreground tw-shadow-xs tw-md:text-sm tw-disabled:pointer-events-none tw-disabled:cursor-not-allowed tw-disabled:opacity-50",
        slotContent || shouldShowToggle ? "!tw-pr-12" : "", // Add right padding only when slot or toggle exists
        themeClasses,
        focusClasses,
        radiusClasses,
        className
      )}
      {...props}
    />
  );

  // If slotContent exists, render with slot
  if (slotContent && React.isValidElement(slotContent)) {
    return (
      <div className="tw-w-full">
        <div className="tw-relative tw-w-full">
          {inputElement}
          <div className="tw-absolute tw-right-3 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-transform tw-items-center tw-justify-center">
            {slotContent.props.children}
          </div>
        </div>
      </div>
    );
  }

  // If no slotContent, but should show password toggle
  if (shouldShowToggle) {
    return (
      <div className="tw-w-full">
        <div className="tw-relative tw-w-full">
          {inputElement}
          <div className="tw-absolute tw-right-3 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-transform tw-items-center tw-justify-center">
            {passwordToggleButton}
          </div>
        </div>
      </div>
    );
  }

  // Default rendering
  return <div className="tw-w-full">{inputElement}</div>;
}

/**
 * Slot component for adding elements to the Input
 *
 * @param {InputSlotProps} props - Slot props
 * @param {React.ReactNode} props.children - Content to display in the slot
 *
 * @example
 * ```tsx
 * <Input.Slot>
 *   <button onClick={handleClick}>Click me</button>
 * </Input.Slot>
 * ```
 */
function InputSlot({ children }: InputSlotProps) {
  return <>{children}</>;
}

InputSlot.displayName = "InputSlot";

// Create compound component
const Input = Object.assign(InputRoot, {
  Slot: InputSlot,
}) as typeof InputRoot & {
  Slot: typeof InputSlot;
  displayName?: string;
};

Input.displayName = "Input";

export { Input };
